import { isEqual } from 'lodash-es';
import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { getProductLimit } from '~/hooks/useProductLimit';
import { useSearchRecommendations } from '~/pages/SearchResults/hooks/useSearchRecommendations';
import { SEARCH_MIN_LENGTH, searchStore } from '~/stores/SearchStore';

export const useSearchBar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [recommendationsData, onSelectRecommendation] =
    useSearchRecommendations();
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const portalInputRef = useRef<HTMLInputElement>(null);

  const [queryString, setQueryString] = useState<string>(
    () => searchParams.get('q') ?? '',
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchWrapperRef, _setSearchWrapperRef] = useState<
    HTMLElement | undefined
  >();
  const [dropdownRef, _setDropdownRef] = useState<HTMLElement | undefined>();
  const isOnSearchResultPage = pathname.startsWith('/search');

  useEffect(() => {
    const currentQueryString = searchParams.get('q');

    if (!searchStore.queryString && currentQueryString) {
      searchStore.setQuery(currentQueryString ?? '');
      searchStore.paginationRequest.debounceLoadPage(1, currentQueryString);
    } else if (searchStore.queryString && isOnSearchResultPage) {
      searchParams.set('q', searchStore.queryString);
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams('', { replace: true });
    }
  }, [isOnSearchResultPage, pathname]);

  const handleOutsideClick = useCallback(
    (e: Event): void => {
      const target = e.target as HTMLElement;

      if (searchWrapperRef?.contains(target) || dropdownRef?.contains(target)) {
        return;
      }

      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        portalInputRef.current?.blur();
      }
    },
    [searchWrapperRef, dropdownRef, isDropdownOpen],
  );

  useEffect(() => {
    window.addEventListener('click', handleOutsideClick);

    return () => {
      window.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const setSearchWrapperRef = (node: HTMLElement | null): void => {
    if (node && !isEqual(node, searchWrapperRef)) {
      _setSearchWrapperRef(node);
    }
  };

  const setDropdownWrapperRef = (node: HTMLElement | null): void => {
    if (node && !isEqual(node, dropdownRef)) {
      _setDropdownRef(node);
    }
  };

  const setQuery = useCallback(
    (str?: string) => {
      if (str?.trim()) {
        searchParams.set('q', str);
      } else {
        searchParams.delete('q');
      }
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleContainerClick = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  const handleStartSearch = () => {
    const activeInput = isDropdownOpen ? portalInputRef : inputRef;
    const inputValue = activeInput.current?.value || '';

    if (inputValue.length >= SEARCH_MIN_LENGTH) {
      setQuery(inputValue);
      searchStore.paginationRequest.debounceLoadPage(1, inputValue);
      setIsDropdownOpen(false);
      activeInput.current?.blur();

      if (!isOnSearchResultPage) {
        navigate('/search', { state: { source: pathname } });
      }
    }
  };

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      const value = (e.target as HTMLInputElement).value;

      const enterCodes = ['Enter', 'NumpadEnter'];
      if (!enterCodes.includes(e.code) || !value) {
        return;
      }

      e.preventDefault();

      handleStartSearch();
    },
    [setQuery],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      setQueryString(value);

      if (value.length >= SEARCH_MIN_LENGTH) {
        searchStore.setQuery(value);
        searchStore.paginationRequest.debounceLoadPage(1, value);
      } else {
        searchStore.reset();
      }
    },
    [],
  );

  const handleHistoryReset = useCallback(() => {
    setTimeout(() => {
      searchStore.clearHistory.run();
    }, 0);
  }, []);

  const handleSearchReset = useCallback(() => {
    searchStore.reset();
    setQuery();
  }, [setQuery]);

  const handleAskGenie = useCallback(() => {
    setIsDropdownOpen(false);
    portalInputRef.current?.blur();
  }, []);

  const handleCloseDropdown = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      e?.stopPropagation();

      setIsDropdownOpen(false);
      portalInputRef.current?.blur();
    },
    [],
  );

  const handleCategoryClick = useCallback(
    (categoryId: string | number) =>
      (e: React.MouseEvent<HTMLButtonElement>) => {
        handleCloseDropdown(e);
        handleSearchReset();
        navigate(`/c/${categoryId}`);
      },
    [handleCloseDropdown, handleSearchReset, navigate],
  );

  const handleSelectRecommendation = useCallback(
    (item: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      setQueryString(item);

      searchStore.setQuery(item);
      searchStore.paginationRequest.debounceLoadPage(1, item);

      onSelectRecommendation(item);

      if (isDropdownOpen && portalInputRef.current) {
        portalInputRef.current.focus();
      } else {
        inputRef.current?.focus();
      }
    },
    [onSelectRecommendation, isDropdownOpen],
  );

  const handleExploreCatalog = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleCloseDropdown(e);
      navigate('/shop');
    },
    [handleCloseDropdown, navigate],
  );

  const filteredSearchResults = useMemo(() => {
    if (!searchStore.result) {
      return null;
    }

    return searchStore.result
      .filter((product) => {
        const { limit } = getProductLimit(product);
        return limit > 0;
      })
      .slice(0, 5);
  }, [searchStore.result]);

  return {
    // Refs
    inputRef,
    portalInputRef,
    setSearchWrapperRef,
    setDropdownWrapperRef,

    // State
    queryString,
    isLoading: searchStore.paginationRequest.isLoading,
    recommendationsData,
    isDropdownVisible: isDropdownOpen,
    searchResults: searchStore.result,
    filteredSearchResults,

    // Handlers
    handleContainerClick,
    handleStartSearch,
    handleKeyUp,
    handleInputChange,
    handleCategoryClick,
    handleSelectRecommendation,
    handleHistoryReset,
    handleCloseDropdown,
    handleAskGenie,
    handleExploreCatalog,
  };
};
